export const isShipmentFullyPaid = (shipment) =>
  Number(shipment?.balance_amount ?? 0) <= 0;

export const isCreditClientShipment = (shipment) => {
  const clientId = shipment?.shipment_client_id;
  if (clientId != null && clientId !== '' && Number(clientId) > 0) return true;
  const mode = String(shipment?.mode_of_payment || shipment?.payment_mode || '').toLowerCase();
  return mode === 'credit';
};

/** Cash or UPI the agent collects at delivery, including demurrage not yet on the balance. */
export const deliveryCollectAmount = (shipment) => {
  if (shipment?.collect_on_delivery != null && shipment.collect_on_delivery !== '') {
    const explicit = Number(shipment.collect_on_delivery);
    if (Number.isFinite(explicit)) return explicit;
  }
  return Number(shipment?.balance_amount || 0) + Number(shipment?.demurrage_pending || 0);
};

export const demurrageDisplayAmount = (shipment) => Number(shipment?.demurrage_amount || 0);

export const amountBeforeDemurrage = (shipment) => {
  if (shipment?.amount_before_demurrage != null && shipment.amount_before_demurrage !== '') {
    const value = Number(shipment.amount_before_demurrage);
    if (Number.isFinite(value)) return value;
  }
  return Number(shipment?.balance_amount || 0);
};

/** Cash or UPI still due from the receiver. Credit-client freight stays on the client. */
export const requiresDeliveryCollection = (shipment) =>
  !isCreditClientShipment(shipment) && deliveryCollectAmount(shipment) > 0.009;

export const QR_TERMINAL_STATUSES = ['delivered', 'rejected', 'returned'];

export const getQrTransitionEligibility = (shipment, targetStatus) => {
  const current = String(shipment?.status || '').toLowerCase();
  const target = String(targetStatus || '').toLowerCase();

  if (QR_TERMINAL_STATUSES.includes(current)) {
    return {
      eligible: false,
      skip: true,
      reason: `Cannot update ${shipment.status} shipment`,
    };
  }

  if (target === 'dispatched') {
    if (current !== 'booked') {
      return {
        eligible: false,
        skip: true,
        reason: 'Only a booked shipment can be dispatched',
      };
    }
    if (shipment.can_dispatch === false) {
      return {
        eligible: false,
        skip: true,
        reason: shipment.dispatch_blocked_reason || 'You can only dispatch shipments you booked',
      };
    }
    return { eligible: true };
  }

  if (target === 'received') {
    if (shipment.can_receive === false) {
      return {
        eligible: false,
        skip: true,
        reason: shipment.receive_blocked_reason
          || 'Only the destination branch can unload this shipment.',
      };
    }
    if (current === 'dispatched' || current === 'received') {
      return {
        eligible: true,
        reapply: current === 'received',
      };
    }
    return {
      eligible: false,
      skip: true,
      reason: `Cannot change from ${shipment.status} to Received`,
    };
  }

  if (target === 'delivered') {
    if (current !== 'received') {
      return {
        eligible: false,
        skip: true,
        reason: `Must be Received first (current: ${shipment.status})`,
      };
    }
    if (requiresDeliveryCollection(shipment)) {
      return { eligible: false, skip: true, reason: 'Payment not complete' };
    }
    return { eligible: true };
  }

  return { eligible: false, skip: true, reason: 'Invalid status' };
};

export const getEligibleShipmentsForQrStatus = (shipments, targetStatus) => {
  const eligible = [];
  const skipped = [];

  for (const shipment of shipments || []) {
    const result = getQrTransitionEligibility(shipment, targetStatus);
    if (result.eligible) {
      eligible.push({ ...shipment, reapply: result.reapply });
    } else {
      skipped.push({ ...shipment, skipReason: result.reason });
    }
  }

  return { eligible, skipped };
};

export const getActiveQrShipments = (shipments) =>
  (shipments || []).filter(
    (s) => !QR_TERMINAL_STATUSES.includes(String(s.status || '').toLowerCase()),
  );

export const normalizeQrResolveResponse = (response) => {
  const data = response?.data || response;
  const shipments = Array.isArray(data?.shipments) && data.shipments.length
    ? data.shipments
    : data?.shipment_id
      ? [data]
      : [];

  return {
    shipments,
    failed: data?.failed || [],
    count: shipments.length,
    scanMethod: data?.scan_method || null,
  };
};

export const getQrStatusOptions = (resolved) => {
  if (!resolved) {
    return { options: [], autoSelect: null, paymentBlocked: false };
  }

  const current = String(resolved.status || '').toLowerCase();

  if (QR_TERMINAL_STATUSES.includes(current)) {
    return {
      options: [],
      autoSelect: null,
      paymentBlocked: false,
      paymentMessage: `${resolved.status} shipments cannot be updated.`,
    };
  }

  if (current === 'booked') {
    if (resolved.can_dispatch === false) {
      return {
        options: [],
        autoSelect: null,
        paymentBlocked: false,
        paymentMessage: resolved.dispatch_blocked_reason
          || 'You can only dispatch shipments you booked',
      };
    }
    return { options: ['Dispatched'], autoSelect: 'Dispatched', paymentBlocked: false };
  }

  if (current === 'dispatched') {
    if (resolved.can_receive === false) {
      return {
        options: [],
        autoSelect: null,
        paymentBlocked: false,
        paymentMessage: resolved.receive_blocked_reason
          || 'Only the destination branch can unload this shipment.',
      };
    }
    return { options: ['Received'], autoSelect: 'Received', paymentBlocked: false };
  }

  if (current === 'received') {
    const paymentMessage = requiresDeliveryCollection(resolved)
      ? 'Collect full payment to mark as Delivered.'
      : isCreditClientShipment(resolved)
        ? 'This is a credit client. Deliver without collecting amount or payment mode.'
        : 'Mark as Delivered.';
    return {
      options: ['Delivered'],
      autoSelect: 'Delivered',
      paymentBlocked: false,
      paymentMessage,
    };
  }

  return { options: [], autoSelect: null, paymentBlocked: false };
};

export const getBulkStatusOptions = (selectedIds, shipments) => {
  const selected = shipments.filter((s) => selectedIds.has(s.shipment_id));
  const active = getActiveQrShipments(selected);

  if (!active.length) {
    return {
      options: [],
      autoSelect: null,
      paymentBlocked: false,
      paymentMessage: 'All scanned shipments are already Delivered or cannot be updated.',
    };
  }

  const hasBookedDispatchable = active.some(
    (s) => String(s.status).toLowerCase() === 'booked' && s.can_dispatch !== false,
  );
  const hasDispatched = active.some(
    (s) => String(s.status).toLowerCase() === 'dispatched',
  );
  const hasReceived = active.some(
    (s) => String(s.status).toLowerCase() === 'received',
  );
  const hasUnpaid = active.some((s) => requiresDeliveryCollection(s));
  const hasCredit = active.some((s) => isCreditClientShipment(s));

  // Booked parcels must dispatch first even if the cart still has older Dispatched rows.
  if (hasBookedDispatchable) {
    return { options: ['Dispatched'], autoSelect: 'Dispatched', paymentBlocked: false };
  }

  if (!hasReceived && hasDispatched) {
    const receivable = active.filter(
      (s) => String(s.status).toLowerCase() === 'dispatched' && s.can_receive !== false,
    );
    if (!receivable.length) {
      const blocked = active.find((s) => s.receive_blocked_reason);
      return {
        options: [],
        autoSelect: null,
        paymentBlocked: false,
        paymentMessage: blocked?.receive_blocked_reason
          || 'Only the destination branch can unload this shipment.',
      };
    }
    return { options: ['Received'], autoSelect: 'Received', paymentBlocked: false };
  }

  if (hasReceived) {
    const allReceived = active.every(
      (s) => String(s.status).toLowerCase() === 'received',
    );
    if (allReceived) {
      return {
        options: ['Delivered'],
        autoSelect: 'Delivered',
        paymentBlocked: false,
        paymentMessage: hasUnpaid
          ? 'Collect full payment to mark as Delivered.'
          : hasCredit
            ? 'This is a credit client. Deliver without collecting amount or payment mode.'
            : 'Mark selected shipments as Delivered.',
      };
    }
    return {
      options: ['Received', 'Delivered'],
      autoSelect: 'Received',
      paymentBlocked: false,
      paymentMessage: hasUnpaid
        ? 'Use Received for Dispatched shipments. Delivered for Received (payment if To-Pay).'
        : hasCredit
          ? 'This is a credit client. Deliver without collecting amount or payment mode.'
          : 'Use Received for Dispatched, Delivered for Received shipments.',
    };
  }

  return { options: ['Received'], autoSelect: 'Received', paymentBlocked: false };
};

export const getQrBatchStatusOptions = (shipments) => {
  if (!shipments?.length) {
    return { options: [], autoSelect: null, paymentBlocked: false };
  }
  if (shipments.length === 1) {
    return getQrStatusOptions(shipments[0]);
  }
  const selectedIds = new Set(shipments.map((s) => s.shipment_id));
  return getBulkStatusOptions(selectedIds, shipments);
};
